/**
 * Assets Service
 * 
 * Handles tracking of images, videos, and prompts in Supabase database.
 * Links assets to Supabase auth users (anonymous or permanent).
 */

import { getSupabaseClient } from './supabase';
import { API_URL } from '../config/apiConfig';
import { cacheManager, CACHE_KEYS, CACHE_TTL } from './cacheManager';

export interface Asset {
  id: string;
  user_id: string;
  type: 'image' | 'video';
  operation_type?: 'upload' | 'image_to_video' | 'image_edit';
  model?: string;
  prompt?: string;
  status: 'processing' | 'completed' | 'failed';
  s3_url?: string;
  storage_url?: string; // Supabase Storage URL
  video_url?: string; // Video URL (unwatermarked for paid users)
  watermarked_video_url?: string; // Watermarked video URL (for free users)
  comfyui_job_id?: string;
  progress?: number; // Progress percentage (0-100)
  created_at: string;
  updated_at: string;
}

export interface CreateAssetParams {
  type: 'image' | 'video';
  operation_type?: 'upload' | 'image_to_video' | 'image_edit';
  model?: string;
  prompt?: string;
  storage_url?: string;
  s3_url?: string;
  status?: 'processing' | 'completed' | 'failed';
}

/**
 * Create or get user record in users table
 * Links Supabase auth user to users table
 */
async function getOrCreateUserRecord(supabaseUserId: string, isAnonymous: boolean): Promise<string> {
  const supabase = getSupabaseClient();
  
  // Check if user record exists
  const { data: existingUser, error: fetchError } = await supabase
    .from('users')
    .select('id')
    .eq('supabase_user_id', supabaseUserId)
    .single();
  
  if (existingUser) {
    return existingUser.id;
  }
  
  if (fetchError && fetchError.code !== 'PGRST116') {
    console.error('❌ [Assets] Error checking user record:', fetchError);
    throw new Error(`Failed to check user record: ${fetchError.message}`);
  }
  
  // Create new user record
  const userData = {
    supabase_user_id: supabaseUserId,
    email: isAnonymous ? `anon_${supabaseUserId}` : null, // Will be updated when user signs up
    credits: 0,
    max_credits: 10,
    subscription_status: 'free',
  };
  
  const { data: newUser, error: createError } = await supabase
    .from('users')
    .insert(userData)
    .select('id')
    .single();
  
  if (createError || !newUser) {
    console.error('❌ [Assets] Failed to create user record:', createError);
    throw new Error(`Failed to create user record: ${createError?.message || 'Unknown error'}`);
  }
  
  return newUser.id;
}

/**
 * Create an asset record in Supabase
 * 
 * @param params - Asset parameters
 * @returns Created asset record
 */
export async function createAsset(params: CreateAssetParams): Promise<Asset> {
  const supabase = getSupabaseClient();
  
  // Get current user
  const { data: { user }, error: userError } = await supabase.auth.getUser();
  
  if (userError || !user) {
    console.error('❌ [Assets] User authentication error:', userError);
    throw new Error(`User not authenticated: ${userError?.message || 'No user found'}`);
  }
  
  // Get or create user record in users table
  const userId = await getOrCreateUserRecord(user.id, user.is_anonymous ?? false);
  
  const assetData = {
    user_id: userId,
    type: params.type,
    operation_type: params.operation_type,
    model: params.model,
    prompt: params.prompt,
    status: params.status || 'processing',
    storage_url: params.storage_url,
    s3_url: params.s3_url,
  };
  
  // Create asset record
  const { data: asset, error } = await supabase
    .from('assets')
    .insert(assetData)
    .select()
    .single();
  
  if (error || !asset) {
    console.error('❌ [Assets] Failed to create asset');
    console.error('❌ [Assets] Error:', error?.code, error?.message);
    throw new Error(`Failed to create asset: ${error?.message || 'Unknown error'}`);
  }
  
  return asset as Asset;
}

/**
 * Update an asset record
 * Tries backend API first (bypasses RLS), falls back to direct Supabase update
 * 
 * @param assetId - Asset ID to update
 * @param updates - Fields to update
 */
export async function updateAsset(
  assetId: string,
  updates: Partial<CreateAssetParams & { status: 'processing' | 'completed' | 'failed'; comfyui_job_id?: string }>
): Promise<Asset> {
  // Try backend API first (bypasses RLS)
  const API_BASE_URL = API_URL;
  try {
    const response = await fetch(`${API_BASE_URL}/api/assets/update`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        id: assetId,  // Backend expects 'id', not 'asset_id'
        ...updates,
      }),
    });
    
    if (response.ok) {
      const result = await response.json();
      return result as Asset;
    } else {
      const error = await response.json().catch(() => ({ error: 'Backend update failed' }));
      console.warn('⚠️ [Assets] Backend update failed, falling back to direct Supabase update:', error);
      // Fall through to direct Supabase update
    }
  } catch (error) {
    console.warn('⚠️ [Assets] Backend API not available, falling back to direct Supabase update:', error);
    // Fall through to direct Supabase update
  }
  
  // Fallback to direct Supabase update
  const supabase = getSupabaseClient();
  
  // First check if asset exists and user has access (don't use .single() to avoid 406 errors)
  const { data: existingAssets, error: checkError } = await supabase
    .from('assets')
    .select('id, user_id')
    .eq('id', assetId);
  
  if (checkError) {
    console.error('❌ [Assets] Error checking asset:', checkError);
    throw new Error(`Failed to check asset: ${checkError.message || 'Unknown error'}`);
  }
  
  if (!existingAssets || existingAssets.length === 0) {
    console.error('❌ [Assets] Asset not found or access denied (RLS blocking)');
    throw new Error('Asset not found or access denied - check RLS policies');
  }
  
  // Asset exists and user has access, proceed with update
  const { data: asset, error } = await supabase
    .from('assets')
    .update({
      ...updates,
      updated_at: new Date().toISOString(),
    })
    .eq('id', assetId)
    .select();
  
  if (error) {
    console.error('❌ [Assets] Failed to update asset:', error);
    throw new Error(`Failed to update asset: ${error.message || 'Unknown error'}`);
  }
  
  if (!asset || asset.length === 0) {
    console.error('❌ [Assets] Update succeeded but no data returned (RLS may be blocking)');
    throw new Error('Update succeeded but no data returned - check RLS policies');
  }
  
  return asset[0] as Asset;
}

/**
 * Get assets for the current user (with caching)
 * Uses backend API for better error handling and reliability
 * 
 * @param type - Optional filter by asset type
 * @returns Array of assets
 */
export async function getUserAssets(type?: 'image' | 'video'): Promise<Asset[]> {
  // Determine cache key based on type
  const cacheKey = type === 'video' ? CACHE_KEYS.USER_VIDEOS :
                   type === 'image' ? CACHE_KEYS.USER_IMAGES :
                   CACHE_KEYS.USER_ASSETS_ALL;
  
  // Check cache first
  const cached = cacheManager.get<Asset[]>(cacheKey);
  if (cached) {
    // Refresh in background
    fetchUserAssetsFromAPI(type).then(data => {
      cacheManager.set(cacheKey, data, CACHE_TTL.USER_VIDEOS);
    }).catch(() => {
      // Silent fail for background refresh
    });
    
    return cached;
  }

  // Cache miss - fetch and store
  const data = await fetchUserAssetsFromAPI(type);
  cacheManager.set(cacheKey, data, CACHE_TTL.USER_VIDEOS);
  return data;
}

/**
 * Internal function to fetch user assets from API
 */
async function fetchUserAssetsFromAPI(type?: 'image' | 'video'): Promise<Asset[]> {
  const supabase = getSupabaseClient();
  
  // Get current user
  const { data: { user }, error: userError } = await supabase.auth.getUser();
  
  if (userError || !user) {
    console.error('❌ [getUserAssets] User not authenticated:', userError?.message);
    throw new Error(`User not authenticated: ${userError?.message || 'No user found'}`);
  }
  
  // Use backend API endpoint for better error handling
  const API_BASE_URL = API_URL;
  
  try {
    // Build query params
    const params = new URLSearchParams();
    params.append('supabase_user_id', user.id);
    
    // Also try email if available
    if (user.email) {
      params.append('email', user.email);
    }
    
    if (type) {
      params.append('type', type);
    }
    
    const url = `${API_BASE_URL}/api/assets?${params.toString()}`;
    
    const response = await fetch(url, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
    });
    
    if (!response.ok) {
      // If backend returns error, try to parse it, otherwise return empty array
      const errorData = await response.json().catch(() => ({}));
      console.warn('⚠️ [getUserAssets] Backend API error fetching assets:', errorData);
      return [];
    }
    
    const data = await response.json();
    return (data.assets || []) as Asset[];
  } catch (error) {
    console.error('Error fetching assets from backend:', error);
    // Fallback to direct Supabase query if backend fails
    try {
      // Get user record
      const { data: userRecord, error: userRecordError } = await supabase
        .from('users')
        .select('id')
        .eq('supabase_user_id', user.id)
        .maybeSingle(); // Use maybeSingle() instead of single() to avoid exceptions
      
      if (userRecordError || !userRecord) {
        // User record doesn't exist yet, return empty array
        return [];
      }
      
      // Build query
      let query = supabase
        .from('assets')
        .select('*')
        .eq('user_id', userRecord.id)
        .order('created_at', { ascending: false });
      
      if (type) {
        query = query.eq('type', type);
      }
      
      const { data: assets, error: assetsError } = await query;
      
      if (assetsError) {
        console.error('Supabase query error:', assetsError);
        return [];
      }
      
      return (assets || []) as Asset[];
    } catch (fallbackError) {
      console.error('Fallback Supabase query also failed:', fallbackError);
      return [];
    }
  }
}

/**
 * Get a single asset by ID
 * 
 * @param assetId - Asset ID
 * @returns Asset record or null
 */
export async function getAsset(assetId: string): Promise<Asset | null> {
  const supabase = getSupabaseClient();
  
  const { data: asset, error } = await supabase
    .from('assets')
    .select('*')
    .eq('id', assetId)
    .single();
  
  if (error) {
    if (error.code === 'PGRST116') {
      // Not found
      return null;
    }
    throw new Error(`Failed to get asset: ${error.message}`);
  }
  
  return asset as Asset;
}

/**
 * Invalidate cached user assets (call after creating/updating/deleting assets)
 */
export function invalidateUserAssetsCache(type?: 'image' | 'video'): void {
  if (type) {
    const cacheKey = type === 'video' ? CACHE_KEYS.USER_VIDEOS : CACHE_KEYS.USER_IMAGES;
    cacheManager.invalidate(cacheKey);
  } else {
    // Invalidate all asset caches
    cacheManager.invalidate(CACHE_KEYS.USER_VIDEOS);
    cacheManager.invalidate(CACHE_KEYS.USER_IMAGES);
    cacheManager.invalidate(CACHE_KEYS.USER_ASSETS_ALL);
  }
}

